import { memo, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAdTypeDto, IdPlacementDto } from '@/services/ws-print/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { usePrintAdTypeOptions } from '../hooks/usePrintAdTypeOptions';

interface PrintAdTypeFilterProps {
  placementId: IdPlacementDto | undefined;
  value: Option<IdAdTypeDto> | null;
  onChange: (value: Option<IdAdTypeDto> | null) => void;
  label?: string;
  dataTestId?: string;
}

export const PrintAdTypeFilter: FC<PrintAdTypeFilterProps> = memo(function PrintAdTypeFilter({
  placementId,
  value,
  onChange,
  label,
  dataTestId,
}) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading, total, searchFilter, setSearchFilter } = usePrintAdTypeOptions({ placementId });

  const handleChange = useCallbackRef((newValue: Option<IdAdTypeDto> | null) => {
    onChange(newValue);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: searchFilter, setFilter: setSearchFilter, total }),
    [searchFilter, setSearchFilter, total],
  );

  return (
    <Filter<IdAdTypeDto>
      label={label || t('common:AD_TYPE')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={serverSideFiltering}
      options={options}
      dataTestId={dataTestId || 'print-ad-type-dropdown'}
    />
  );
});
